import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ROUTES } from '~shared/config/routes';
import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import { useIsDesktop } from '~shared/lib/responsive';
import type { DataTableSorting } from '~shared/ui/data-table';
import { DataTable } from '~shared/ui/data-table';

import { Button } from '@breezeventures/ui-kit/button';
import { Modal } from '@breezeventures/ui-kit/modal';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import { FolderDocuments as FolderDocumentsIllustration } from '@breezeventures/ui-kit/next/illustrations/medium/folder-documents';
import { Typography } from '@breezeventures/ui-kit/typography';
import type { VisibilityState } from '@tanstack/react-table';

import { OrderDetails } from './order-details/order-details';

import { columns } from '../lib/columns';
import type { OrderTableData } from '../model/orders-table-data';

type Props = {
  orders: OrderTableData[];
  total: number;
  isLoading: boolean;
  showOnboarding: boolean;
  pagination: { page: number; perPage: number };
  onPageChange: (page: number) => void;
  onPageSizeChange: (perPage: number) => void;
  sorting?: DataTableSorting;
  onSortingChange?: (sorting: DataTableSorting) => void;
  columnVisibility?: VisibilityState;
};

export const OrdersTable = ({
  orders,
  total,
  isLoading,
  showOnboarding,
  pagination,
  onPageChange,
  onPageSizeChange,
  sorting,
  onSortingChange,
  columnVisibility,
}: Props) => {
  const [orderDetailsOpened, setOrderDetailsOpened] = useState(false);
  const lastSelectedOrderRef = useRef<OrderTableData | null>(null);
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const handleRowClick = (item: OrderTableData) => {
    lastSelectedOrderRef.current = item;
    setOrderDetailsOpened(true);
  };

  if (showOnboarding) {
    return (
      <div
        className={`
          flex h-full w-full flex-col items-center justify-center gap-8 py-10
        `}
      >
        <FolderDocumentsIllustration alt="" className="size-29" />
        <Typography.Title size="s" tag="h3">
          No orders yet
        </Typography.Title>
        <Button
          size="m"
          variant="primary"
          onClick={() => navigate(ROUTES.orders.create)}
        >
          <Button.Icon>
            <Icon name="plus" size={24} version="v1" />
          </Button.Icon>
          Add order
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <DataTable
        data={orders}
        columns={columns}
        getRowId={(row) => row.orderId}
        onRowClick={handleRowClick}
        showRowChevron={!isDesktop}
        isLoading={isLoading}
        minWidth={isDesktop ? 990 : 0}
        columnVisibility={columnVisibility}
        emptyState="We didn't find anything"
        sorting={sorting}
        onSortingChange={isDesktop ? onSortingChange : undefined}
        pagination={
          total > 0
            ? {
                page: pagination.page,
                pageSize: pagination.perPage,
                itemsTotal: total,
                onPageChange,
                onPageSizeChange,
              }
            : undefined
        }
      />

      <Modal
        opened={orderDetailsOpened}
        onClose={() => setOrderDetailsOpened(false)}
        title="Order details"
        size="wide"
      >
        <OrderDetails order={nonNullableValue(lastSelectedOrderRef.current)} />
      </Modal>
    </div>
  );
};
